// src/pages/HistoryShuihe.jsx
import React, { useState, useEffect, useLayoutEffect } from "react";
import { Box, Container, Tabs, Tab } from "@mui/material";
import { useLocation } from "react-router-dom";
import { getPrimaryColor } from "../../../../GlobalFunction/Theme";
import HeroWave from "../../../../GlobalFunction/HeroWave";

// NOTE: all imported as DEFAULT components (no curly braces)
import HistoryCompanyIntro from "./HistoryCompanyIntro";
import HistoryPhilosophy from "./HistoryPhilosophy";
import HistoryTimeline from "./HistoryTimeline";
import HistoryVision from "./HistoryVision";

// 🔹 language cookie util
import { getLangFromCookie } from "../../../../../Utils/LangCookie";

// shared style config for this page
const fonts = {
  EN: "LINE Seed Sans",
  ZH: "LINE Seed TW",
  JP: "LINE Seed JP",
};
const HISTORY_BG_COLOR = "#fffffc";

const SUPPORTED_LANGS = ["ZH", "EN", "JP"];

// safe wrapper so we don’t blow up in environments without `document`
function safeGetLang() {
  if (typeof document === "undefined") return "ZH";
  const c = getLangFromCookie();
  const upper = (c || "ZH").toUpperCase();
  return SUPPORTED_LANGS.includes(upper) ? upper : "ZH";
}

// hook: listen to app.lang cookie + "app:languageChanged" event
function useLangFromCookie() {
  const [lang, setLang] = useState(() => safeGetLang());

  useEffect(() => {
    const refresh = () => {
      setLang((prev) => {
        const next = safeGetLang();
        return next === prev ? prev : next;
      });
    };

    // initial sync
    refresh();

    // listen to your custom event (fired in setLangCookie)
    const handler = () => refresh();
    window.addEventListener("app:languageChanged", handler);

    // light polling fallback in case cookie changes without event
    const id = setInterval(refresh, 1000);

    return () => {
      window.removeEventListener("app:languageChanged", handler);
      clearInterval(id);
    };
  }, []);

  return lang;
}

// ------------ tab labels (ZH / EN / JP) ------------
const TAB_CONFIG = [
  {
    key: "company",
    labels: {
      ZH: "公司介紹",
      EN: "Profile",
      JP: "会社概要",
    },
  },
  {
    key: "philosophy",
    labels: {
      ZH: "企業理念 & 願景",
      EN: "Vision & Philosophy",
      JP: "企業ビジョン&理念",
    },
  },
];

// helper: read tab index ONLY from location.state.tab / TAB, or fallback
function getTabFromLocation(location, fallback = 0) {
  let tabIndex = fallback;

  if (location && location.state) {
    const st = location.state;
    const raw = st.tab ?? st.TAB;
    if (raw !== undefined) {
      const n = Number(raw);
      if (!Number.isNaN(n) && n >= 0 && n <= 1) {
        tabIndex = n;
      }
    }
  }

  return tabIndex;
}

export default function HistoryShuihe({ mode = "b2b", TAB = 0 }) {
  const location = useLocation();
  const lang = useLangFromCookie(); // <- ZH / EN / JP

  const [pageReadyKey, setPageReadyKey] = useState("");
  const isPageReady = pageReadyKey === location.key;

  useLayoutEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    let cancelled = false;

    setPageReadyKey("");

    const previousScrollRestoration =
      "scrollRestoration" in window.history
        ? window.history.scrollRestoration
        : null;

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const forceTop = () => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });

      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
        document.scrollingElement.scrollLeft = 0;
      }

      document.documentElement.scrollTop = 0;
      document.documentElement.scrollLeft = 0;

      document.body.scrollTop = 0;
      document.body.scrollLeft = 0;

      const root = document.getElementById("root");
      if (root) {
        root.scrollTop = 0;
        root.scrollLeft = 0;
      }

      const main = document.querySelector("main");
      if (main) {
        main.scrollTop = 0;
        main.scrollLeft = 0;
      }
    };

    const revealPage = () => {
      forceTop();

      if (!cancelled) {
        setPageReadyKey(location.key);
      }
    };

    forceTop();

    const frameOne = window.requestAnimationFrame(() => {
      forceTop();

      window.requestAnimationFrame(() => {
        revealPage();
      });
    });

    const timeoutOne = window.setTimeout(forceTop, 0);
    const timeoutTwo = window.setTimeout(revealPage, 80);
    const timeoutThree = window.setTimeout(forceTop, 180);
    const timeoutFour = window.setTimeout(forceTop, 360);
    const timeoutFive = window.setTimeout(forceTop, 700);

    return () => {
      cancelled = true;

      window.cancelAnimationFrame(frameOne);

      window.clearTimeout(timeoutOne);
      window.clearTimeout(timeoutTwo);
      window.clearTimeout(timeoutThree);
      window.clearTimeout(timeoutFour);
      window.clearTimeout(timeoutFive);

      if (
        previousScrollRestoration &&
        "scrollRestoration" in window.history
      ) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, [location.pathname, location.key]);

  const [tab, setTab] = useState(() => getTabFromLocation(location, TAB ?? 0));

  const primary = getPrimaryColor(mode);

  const handleTabChange = (_e, v) => setTab(v);

  // When navigation state changes (e.g. navigate("/b2b/公司簡介", { state: { tab: 1 } }))
  useEffect(() => {
    setTab(getTabFromLocation(location, TAB ?? 0));
  }, [location, TAB]);

  const currentTabConfig = TAB_CONFIG[tab] || TAB_CONFIG[0];
  const heroTitle =
    (currentTabConfig.labels && currentTabConfig.labels[lang]) ||
    currentTabConfig.labels.ZH;

  return (
    <Box sx={{ bgcolor: HISTORY_BG_COLOR, overflowX: "hidden" }}>

      {/* Tabs */}
      <Box
        sx={{
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          bgcolor: HISTORY_BG_COLOR,
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            maxWidth: 1280,
            mx: "auto",
            px: { xs: 2, md: 3 },
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="inherit"
            TabIndicatorProps={{
              sx: {
                height: 3,
                bgcolor: primary,
              },
            }}
          >
            {TAB_CONFIG.map((cfg, i) => {
              const label =
                (cfg.labels && cfg.labels[lang]) || cfg.labels.ZH || "";
              return (
                <Tab
                  key={cfg.key}
                  label={label}
                  sx={{
                    fontFamily: fonts[lang],
                    fontSize: { xs: 15, sm: 20, md: 22, lg: 20 },
                    fontWeight: tab === i ? 700 : 400,
                    color: tab === i ? primary : "#555",
                  }}
                />
              );
            })}
          </Tabs>
        </Container>
      </Box>

      {/* Tab content */}
      {tab === 0 && (
        <>
          <HistoryCompanyIntro
            mode={mode}
            fonts={fonts[lang]}
            bg={HISTORY_BG_COLOR}
            rail={primary}
            lang={lang}
          />

          <HistoryTimeline
            mode={mode}
            fonts={fonts[lang]}
            bg={HISTORY_BG_COLOR}
            rail={primary}
            lang={lang}
          />
        </>
      )}

      {tab === 1 && (
        <HistoryVision
          mode={mode}
          fonts={fonts[lang]}
          bg={HISTORY_BG_COLOR}
          rail={primary}
          lang={lang}
        />
      )}
    </Box>
  );
}
